import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { styled, Theme, CSSObject } from '@mui/material/styles';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Toolbar,
  Badge,
  MenuItem,
  Menu,
  IconButton,
  Collapse,
  Link,
  Avatar,
} from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';

import {
  ArrowLeftDoubleIcon,
  CalendarIcon,
  CalendarActiveIcon,
  ClockIcon,
  ClockActiveIcon,
  DashboardIcon,
  DashboardActiveIcon,
  DocumentIcon,
  DocumentActiveIcon,
  ListIcon,
  ListActiveIcon,
  NotesIcon,
  NotesActiveIcon,
  SettingIcon,
  SettingActiveIcon,
  NotificationIcon,
  ArrowDownMenuIcon,
  LogOutIcon,
  PlusIcon,
  TrashIcon,
  PenIcon,
  UserIcon,
  SendMailIcon,
  SendMailActiveIcon,
  TaskIcon,
  TaskActiveIcon,
  InformationIcon,
} from '~/assets/icons';

import { useTranslation } from 'react-i18next';
import { BaseAutocomplete, BaseImage } from '~/components/common';
import logo from '~/assets/images/logo.png';
import styles from './Main.module.scss';
import classNames from 'classnames/bind';
import React, { ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { adminRouteAbsolute } from '~/utils/constants/route';
import { StorageEnum } from '~/utils/enum';
import SelectFranchise from '~/components/franchise/select-franchise/SelectFranchise';
import ModalAlertLogout from '~/components/common/modal-alert-logout/ModalAlertLogout';
import { handleGetProfile } from '~/thunks/auth/authThunk';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import usePermission from '~/hooks/usePermission';
import _ from 'lodash';
import { PERMISSIONS } from '~/utils/constants/permission';
import { handleGetCommonSearch } from '~/thunks/common-search/commonSearchThunk';
import { ICommonSearchItem } from '~/utils/interface/commonField';
import dayjs from 'dayjs';
import { JOB } from '~/utils/constants/jobs';
import InfiniteScroll from 'react-infinite-scroll-component';

import { QUEUE_STATUS_PROCESS, ROWS_PER_PAGE } from '~/utils/constants/common';
import { NOTI_ACTION_CREATE, NOTI_ACTION_DELETE, NOTI_ACTION_UPDATE } from '~/utils/constants/notification';
import { handleGetSelectionCompanies } from '~/thunks/company/companyThunk';
import { handleGetNotificationList, handleUpdateNotificationList } from '~/thunks/notification/notificationThunk';
import { notificationActions } from '~/thunks/notification/notificationSlice';
import useQuickbook from '~/hooks/useQuickbook';
import { RingCentralContext } from '~/context/RingCentralContext';
import { stringToColor } from '~/utils/helper';
import { commonActions } from '~/thunks/common/commonSlice';

type AppBarProps = MuiAppBarProps & {
  open?: boolean;
};

type TSubMenu = {
  name: string;
  url: string;
  permission?: string;
};

type TSidebar = {
  name: string;
  icon: ReactNode;
  activeIcon: ReactNode;
  url: string;
  subMenu?: Array<TSubMenu>;
  permission?: string;
};

type TSubMenuOpen = {
  [field: string]: boolean;
};

const sidebarList: Array<TSidebar> = [
  {
    name: 'dashboard',
    icon: <DashboardIcon />,
    activeIcon: <DashboardActiveIcon />,
    url: '/admin/dashboard',
  },
  {
    name: 'jobs',
    icon: <ListIcon />,
    activeIcon: <ListActiveIcon />,
    url: '/admin/jobs',
    permission: PERMISSIONS.project_view,
  },

  {
    name: 'schedule',
    icon: <CalendarIcon />,
    activeIcon: <CalendarActiveIcon />,
    url: '/admin/schedule',
    permission: PERMISSIONS.schedule_view,
  },
  {
    name: 'timeClock',
    icon: <ClockIcon />,
    activeIcon: <ClockActiveIcon />,
    url: '/admin/time-clocks',
    permission: PERMISSIONS.timeClock_view,
  },
  // {
  //   name: 'estimates',
  //   icon: <CaculatorIcon />,
  //   activeIcon: <CaculatorActiveIcon />,
  //   url: '/admin/estimates',
  //   subMenu: [
  //     // {
  //     //   name: 'priceComponents',
  //     //   url: '/admin/price-components',
  //     //   permission: PERMISSIONS.priceComponent,
  //     // },
  //     // {
  //     //   name: 'prices',
  //     //   url: '/admin/prices',
  //     //   permission: PERMISSIONS.price,
  //     // },
  //   ],
  // },
  {
    name: 'invoices',
    icon: <DocumentIcon />,
    activeIcon: <DocumentActiveIcon />,
    url: '/admin/invoices',
    permission: PERMISSIONS.invoice_view,
  },
  {
    name: 'crm',
    icon: <SettingIcon />,
    activeIcon: <SettingActiveIcon />,
    url: '/admin/crm',
    permission: PERMISSIONS.crm_view,
    subMenu: [
      {
        name: 'organization',
        url: '/admin/customers',
        permission: PERMISSIONS.organization_view,
      },

      {
        name: 'contacts',
        url: '/admin/contacts',
        permission: PERMISSIONS.contact_view,
      },
      {
        name: 'pipeline',
        url: '/admin/crm',
        permission: PERMISSIONS.crm_view,
      },
    ],
  },
  {
    name: 'campaigns',
    icon: <SendMailIcon />,
    activeIcon: <SendMailActiveIcon />,
    url: '/admin/campaigns',
    permission: PERMISSIONS.campaign_view,
  },
  {
    name: 'reports',
    icon: <NotesIcon />,
    activeIcon: <NotesActiveIcon />,
    url: '/admin/reports',
    subMenu: [
      {
        name: 'reportActivity',
        url: '/admin/reports/activities',
        permission: PERMISSIONS.report_view,
      },
      {
        name: 'reportLead',
        url: '/admin/reports/leads',
        permission: PERMISSIONS.report_view,
      },
      {
        name: 'sourceReferral',
        url: '/admin/reports/source-referrals',
        permission: PERMISSIONS.report_view,
      },
      {
        name: 'project',
        url: '/admin/reports/projects',
        permission: PERMISSIONS.report_view,
      },

      {
        name: 'closingRate',
        url: '/admin/reports/closing-rate',
        permission: PERMISSIONS.report_view,
      },
      {
        name: 'staffSale',
        url: '/admin/reports/staff-sale',
        permission: PERMISSIONS.report_view,
      },

      {
        name: 'franchiseMKT',
        url: '/admin/reports/franchise-marketing',
        permission: PERMISSIONS.report_view,
      },
      {
        name: 'sale',
        url: '/admin/reports/sale',
        permission: PERMISSIONS.report_view,
      },
      {
        name: 'timeClockReport',
        url: '/admin/reports/time-clock',
        permission: PERMISSIONS.report_view,
      },
    ],
  },
  {
    name: 'tasks',
    icon: <TaskIcon />,
    activeIcon: <TaskActiveIcon />,
    url: '/admin/tasks',
    permission: PERMISSIONS.companyTask_view,
  },
  {
    name: 'template',
    icon: <DocumentIcon />,
    activeIcon: <DocumentActiveIcon />,
    url: '/admin/templates',
    subMenu: [
      {
        name: 'estimate',
        url: '/admin/templates/estimate',
        permission: PERMISSIONS.estimateTemplate_view,
      },
      {
        name: 'openingStatements',
        url: '/admin/templates/opening-statements',
        permission: PERMISSIONS.openingStatementTemplate_view,
      },
      {
        name: 'termCondition',
        url: '/admin/templates/terms-conditions',
        permission: PERMISSIONS.estimateTermTemplate_view,
      },
      {
        name: 'email',
        url: '/admin/templates/email',
        permission: PERMISSIONS.emailTemplate_view,
      },
    ],
  },
  {
    name: 'settings',
    icon: <SettingIcon />,
    activeIcon: <SettingActiveIcon />,
    url: '/admin/settings',
    subMenu: [
      {
        name: 'franchise',
        url: '/admin/franchise',
        permission: PERMISSIONS.company_view,
      },

      {
        name: 'users',
        url: '/admin/users',
        permission: PERMISSIONS.account_view,
      },
      {
        name: 'priceItems',
        url: '/admin/price-items',
        permission: PERMISSIONS.priceItem_view,
      },
      {
        name: 'quickbooks',
        url: '/admin/quickbooks',
        permission: PERMISSIONS.quickbooks_view,
      },
      {
        name: 'ringCentral',
        url: '/admin/ring-central',
        permission: PERMISSIONS.ringCentral_connect,
      },
      {
        name: 'location',
        url: '/admin/location',
        permission: PERMISSIONS.location,
      },
      {
        name: 'companyRoles',
        url: '/admin/company-roles',
        permission: PERMISSIONS.companyRole_view,
      },
      {
        name: 'equipment',
        url: '/admin/equipments',
        permission: PERMISSIONS.equipment_view,
      },
      {
        name: 'jobStatuses',
        url: '/admin/job-statuses',
        permission: PERMISSIONS.projectStatus_view,
      },
      {
        name: 'jobTypes',
        url: '/admin/job-types',
        permission: PERMISSIONS.jobType_view,
      },
      {
        name: 'crmStatuses',
        url: '/admin/crm-statuses',
        permission: PERMISSIONS.crmStatusSetting_view,
      },
    ],
  },
];

const cx = classNames.bind(styles);

const drawerWidth = 260;
const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(8)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(9)} + 1px)`,
  },
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}));

const handleBeforeUnload = (event: any) => {
  event.preventDefault();
  event.returnValue = '';
};

export default function MiniDrawer() {
  //#region Destructuring Props
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/layout', 'admin/auth']);
  const location = useLocation();
  const dispatch = useAppDispatch();
  const permissions = usePermission();
  const { quickbook } = useQuickbook();
  const { setIsJoinPage, setIsShowWidget } = useContext(RingCentralContext);
  //#endregion Declare Hook

  //#region Selector
  const { queueList, isLoading } = useAppSelector((state) => state.common);
  //#endregion Selector

  //#region Declare State
  const [open, setOpen] = useState<boolean | undefined>(true);

  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [activeScreen, setActiveScreen] = useState<string>('dashboard');
  const [hasPermission, setHasPermission] = useState<boolean>(true);
  const [menuAnchorElement, setmenuAnchorElement] = useState<null | HTMLElement>(null);
  const [menuListNotification, setmenuListNotification] = useState<null | HTMLElement>(null);
  const [isLoggedIn] = useState<boolean>(!!localStorage.getItem(StorageEnum.ACCESS_TOKEN));
  const { profile } = useAppSelector((state) => state.auth);
  const { notificationList, filterList, needRefreshData } = useAppSelector((state) => state.notification);
  const [hasMore, setHasMore] = useState(true);
  const isMenuOpen = Boolean(menuAnchorElement);
  const isMenuOpenListNotification = Boolean(menuListNotification);
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState<TSubMenuOpen>({});
  const { options } = useAppSelector((state) => state.commonSearch);

  const permissionSidebar: Array<TSidebar> = useMemo(() => {
    const permissionItems: Array<TSidebar> = [];
    sidebarList.forEach((sidebar) => {
      const permissionItem = _.clone(sidebar);
      let hasPermission = false;
      if (permissionItem?.subMenu?.length) {
        permissionItem.subMenu = permissionItem.subMenu.filter(
          (subMenu) => !subMenu.permission || (subMenu.permission && permissions[subMenu.permission])
        );

        hasPermission = !!permissionItem.subMenu.length;
      } else {
        hasPermission =
          !permissionItem.permission || (!!permissionItem.permission && permissions[permissionItem.permission]);
      }

      if (hasPermission) permissionItems.push(permissionItem);
    });
    return permissionItems;
  }, [permissions]);
  //#endregion Declare State
  //#region Implement Hook
  useEffect(() => {
    if (isLoggedIn) {
      setIsJoinPage && setIsJoinPage(true);
    }

    return () => {
      setIsJoinPage && setIsJoinPage(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let matchedScreen;

    for (let menu of sidebarList) {
      if (menu?.subMenu) {
        matchedScreen = menu?.subMenu.find((subMenu) => location.pathname.indexOf(subMenu.url) > -1);
        if (matchedScreen) break;
      }

      if (location.pathname.indexOf(menu.url) > -1) {
        matchedScreen = menu;
        break;
      }
    }

    if (matchedScreen) {
      setActiveScreen(matchedScreen.name);
      if (Object.keys(permissions).length) {
        setHasPermission(
          !matchedScreen.permission || !!(matchedScreen.permission && permissions[matchedScreen.permission])
        );
      }
    }
  }, [location, permissions]);

  useEffect(() => {
    if (dispatch) {
      dispatch(handleGetProfile());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needRefreshData]);
  useEffect(() => {
    dispatch(handleGetSelectionCompanies());

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    window.removeEventListener('beforeunload', handleBeforeUnload);

    if (queueList.findIndex((queue) => queue.status === QUEUE_STATUS_PROCESS) > -1) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }
  }, [queueList]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setmenuAnchorElement(event.currentTarget);
  };
  const handleClose = () => {
    setmenuAnchorElement(null);
  };

  const handleOpenListNotification = async (event: React.MouseEvent<HTMLButtonElement>) => {
    setmenuListNotification(event.currentTarget);
    await dispatch(handleUpdateNotificationList({}));
    dispatch(notificationActions.changeNeedRefreshData(true));
  };

  const handleCloseMenuListNotification = () => {
    setmenuListNotification(null);
  };

  const hanleNavigateMenuAnchor = async (url: string) => {
    handleClose();
    handleCloseMenuListNotification();
    await dispatch(handleGetNotificationList(filterList)).unwrap();
    navigate(url);
  };

  const handleNavigateDrawer = (url: string) => {
    navigate(url);
  };

  const handleOpenModalLogOut = () => {
    setIsDialogOpen(true);
  };
  const companyId = localStorage.getItem(StorageEnum.COMPANY_ID);

  const handleCancelLogOut = () => {
    setIsDialogOpen(false);
  };

  const handleConfirmLogOut = () => {
    localStorage.removeItem(StorageEnum.ACCESS_TOKEN);
    localStorage.removeItem(StorageEnum.REFRESH_TOKEN);
    localStorage.removeItem(StorageEnum.COMPANY_ID);
    window.location.href = '/admin/login';
    setIsShowWidget && setIsShowWidget(false);
    setIsDialogOpen(false);
  };

  const loadMore = () => {
    dispatch(
      notificationActions.setFilterList({
        page: filterList.page + 1,
        limit: ROWS_PER_PAGE,
      })
    );
  };

  // common search

  const handleSearch = (searchKey: string) => {
    dispatch(handleGetCommonSearch({ search: searchKey }));
  };

  const handleSelect = (selectedOption: ICommonSearchItem) => {
    let redirectUrl = `/admin/customers/detail/${selectedOption.id}`;

    if (selectedOption.group === 'contact') {
      redirectUrl = `/admin/contacts/detail/${selectedOption.id}`;
    }

    if (selectedOption.group === 'project') {
      redirectUrl = `/admin/jobs/detail/${selectedOption.id}`;
    }
    navigate(redirectUrl);
  };
  useEffect(() => {
    const fetchNotificationList = async () => {
      try {
        await dispatch(handleGetNotificationList(filterList))
          .unwrap()
          .then((result) => {
            if (result.responses.length === 0) {
              setHasMore(false);
            } else {
              setHasMore(true);
            }
          });
      } catch (error) {}
    };

    fetchNotificationList();
  }, [dispatch, filterList]);
  //#endregion Handle Function

  return isLoggedIn ? (
    <Box className={cx('layout-main')}>
      {isLoading && <Box className='h-full w-full z-[100000] opacity-0 cursor-wait absolute top-0 left-0'></Box>}
      <AppBar position='fixed' open={open} color='default'>
        <Box className={cx('flex justify-between px-[40px] gap-[16px]', { 'pl-0': !open })}>
          <Toolbar className='p-0 w-full justify-between gap-[16px]'>
            <IconButton
              color='inherit'
              onClick={() => setOpen(true)}
              edge='start'
              className={cx('p-0 w-[40px] h-[40px]', { hidden: open, ' ml-[20px] mr-[40px]': !open })}
            >
              <ArrowLeftDoubleIcon className='w-[11px]' />
            </IconButton>
            <Box className='hidden lg:block w-[400px]  '>
              <BaseAutocomplete onSearch={handleSearch} onSelected={handleSelect} options={options} />
            </Box>
            {!quickbook?.isQuickbookConnected && (
              <Link
                className='underline text-[#e8625a] bg-[#e8625a0d] p-[10px] rounded-[8px] mr-auto'
                onClick={() => {
                  navigate(adminRouteAbsolute.quickbooks);
                }}
              >
                {t('quickbookNotConnected')}
              </Link>
            )}
            <Box className={cx('franchise-select', 'ml-auto', 'hidden lg:block')}>
              <SelectFranchise name='status' value={companyId ?? undefined} />
            </Box>
          </Toolbar>
          <Box sx={{ display: { xs: 'none', md: 'flex' } }} className={cx('right-toolbar')}>
            <IconButton
              size='large'
              color='inherit'
              className={cx('badge-button')}
              onClick={handleOpenListNotification}
            >
              <Badge
                badgeContent={(profile.countNotifications || 0) + queueList.filter((queue) => !queue.isRead).length}
                color='info'
                className={cx('notification-badge')}
              >
                <NotificationIcon />
              </Badge>
            </IconButton>
            <Box className='flex flex-row gap-[16px] h-[38px] whitespace-nowrap '>
              <Box className='text-right justify-between flex-col hidden sm:flex'>
                <Box className='text-[#222732] font-semibold max-w-[80px] truncate'>{profile.name}</Box>
                <Box className='text-[#9AA4BA]'>{t(`${profile.role}`, { ns: 'admin/auth' })}</Box>
              </Box>
              {profile.avatar ? (
                <Avatar className={cx('image')} alt={profile?.name} src={profile.avatar} />
              ) : (
                <Avatar className={cx('image')} sx={{ bgcolor: stringToColor(profile?.name ?? '') }}>
                  {profile?.name?.[0]}
                </Avatar>
              )}
              <Box className='flex items-center'>
                <IconButton onClick={handleClick} className='flex justify-center w-[20px] h-[20px] p-0'>
                  <ArrowDownMenuIcon className='w-[13px]' />
                </IconButton>

                <Menu id='main-menu' anchorEl={menuAnchorElement} open={isMenuOpen} onClose={handleClose}>
                  <Box className='flex flex-row gap-[12px] h-[38px]'>
                    {profile.avatar ? (
                      <Avatar className={cx('image')} alt={profile?.name} src={profile.avatar} />
                    ) : (
                      <Avatar className={cx('image')} sx={{ bgcolor: stringToColor(profile?.name ?? '') }}>
                        {profile?.name?.[0]}
                      </Avatar>
                    )}
                    <Box className='flex justify-between flex-col'>
                      <Box className='text-[#222732] font-semibold'>{profile.name}</Box>
                      <Box className='text-[#9AA4BA]'>{profile.email}</Box>
                    </Box>
                  </Box>
                  <Divider className='mt-[16px] mb-[20px]' />
                  <MenuItem onClick={() => hanleNavigateMenuAnchor(adminRouteAbsolute.profile)}>
                    <UserIcon className='h-[16px]' />
                    {t('header.editProfile')}
                  </MenuItem>
                  <MenuItem onClick={() => hanleNavigateMenuAnchor(adminRouteAbsolute.accountSetting)}>
                    <SettingIcon className='h-[16px]' />
                    {t('header.accountSetting')}
                  </MenuItem>
                  <MenuItem onClick={() => handleOpenModalLogOut()}>
                    <LogOutIcon className='h-[16px]' />
                    {t('header.logOut')}
                  </MenuItem>
                  <ModalAlertLogout open={isDialogOpen} onCancel={handleCancelLogOut} onConfirm={handleConfirmLogOut} />
                </Menu>

                {/* Notifications */}

                <Menu
                  id='main-menu'
                  anchorEl={menuListNotification}
                  open={isMenuOpenListNotification}
                  onClose={handleCloseMenuListNotification}
                  className={cx('menuListNotification')}
                >
                  <InfiniteScroll
                    dataLength={notificationList.length}
                    next={loadMore}
                    hasMore={hasMore}
                    height={400}
                    loader={''}
                  >
                    {queueList.length > 0 &&
                      queueList.map((item, index) => (
                        <MenuItem
                          key={index}
                          className={`flex flex-row gap-[12px] h-[38px]p-[5px] ${item.isRead ? 'red' : 'bg-[#f0f7ff]'}`}
                          onClick={() => {
                            dispatch(
                              commonActions.setQueue({
                                id: item.id,
                                isRead: true,
                              })
                            );

                            if (item.navigateUrl) {
                              hanleNavigateMenuAnchor(item.navigateUrl);
                            }
                          }}
                        >
                          <Box className={cx('container-item')}>
                            <InformationIcon className='w-[15px] h-[15px] [&>path]:stroke-[#007AFC]' />
                          </Box>
                          <Box className='flex justify-between flex-col'>
                            <Box className='text-[14px]'>{item.title}</Box>
                            <Box className='text-[10px] text-[#9AA4BA]'>
                              {dayjs(item.id).format('MMM DD YYYY h:mm a')}
                            </Box>
                          </Box>
                          <Divider className='!mt-[0px] !mb-[0px]' />
                        </MenuItem>
                      ))}
                    {notificationList.length > 0 &&
                      notificationList.map((item, index) => (
                        <React.Fragment key={index}>
                          <MenuItem
                            className={`flex flex-row gap-[12px] h-[38px]p-[5px] ${
                              item.isRead ? 'red' : 'bg-[#f0f7ff]'
                            }`}
                            onClick={() => {
                              hanleNavigateMenuAnchor(
                                item.activity.groupType === JOB
                                  ? `${adminRouteAbsolute.jobDetail.replace(':id', item.activity.groupId)}`
                                  : `${adminRouteAbsolute.customerDetail.replace(':id', item.activity.groupId)}`
                              );
                            }}
                          >
                            <Box className={cx('container-item')}>
                              {item.activity.action === NOTI_ACTION_CREATE ? (
                                <PlusIcon className='w-[15px] h-[15px] [&>path]:stroke-[#007AFC]' />
                              ) : item.activity.action === NOTI_ACTION_UPDATE ? (
                                <PenIcon className='w-[15px] h-[15px]' />
                              ) : item.activity.action === NOTI_ACTION_DELETE ? (
                                <TrashIcon className='w-[15px] h-[15px] [&>path]:stroke-[#FF0000]' />
                              ) : (
                                ''
                              )}
                            </Box>
                            <Box className='flex justify-between flex-col'>
                              <Box className='text-[14px]'>{item.title}</Box>
                              <Box className='text-[10px] text-[#9AA4BA]'>
                                {dayjs(item.createdAt).format('MMM DD YYYY h:mm a')}
                              </Box>
                            </Box>
                          </MenuItem>
                          <Divider className='!mt-[0px] !mb-[0px]' />
                        </React.Fragment>
                      ))}
                    {!notificationList.length && !queueList.length && (
                      <MenuItem className='flex justify-center items-center w-[300px] '>
                        <Box className='note-container-item'> {t('noNotification', { ns: 'admin/job' })}</Box>
                      </MenuItem>
                    )}
                  </InfiniteScroll>
                </Menu>
              </Box>
            </Box>
          </Box>
        </Box>
      </AppBar>
      <Drawer variant='permanent' open={open} className='sidebar'>
        <DrawerHeader className='h-[82px] flex justify-between px-[24px]'>
          <BaseImage src={logo} className='h-[61px]' />
          <IconButton onClick={() => setOpen(false)} className={cx('p-0 w-[40px] h-[40px]', { hidden: !open })}>
            <ArrowLeftDoubleIcon className='w-[11px]' />
          </IconButton>
        </DrawerHeader>
        <Divider />
        <List>
          {permissionSidebar.map((item, index) => (
            <React.Fragment key={index}>
              <ListItem
                className={cx({
                  active: activeScreen === item.name || item.subMenu?.find((subMenu) => subMenu.name === activeScreen),
                })}
                disablePadding
                onClick={() => {
                  if (item?.subMenu) {
                    const currentMenuOpen = { ...menuOpen };
                    currentMenuOpen[item.name] = !currentMenuOpen[item.name];
                    setMenuOpen(currentMenuOpen);
                    return;
                  }
                  item.url && handleNavigateDrawer(item.url);
                }}
              >
                <ListItemButton>
                  <ListItemIcon>
                    {activeScreen === item.name || item.subMenu?.find((subMenu) => subMenu.name === activeScreen)
                      ? item.activeIcon
                      : item.icon}
                  </ListItemIcon>
                  <ListItemText primary={t(`sidebar.${item.name}`)} sx={{ opacity: open ? 1 : 0 }} />
                </ListItemButton>
              </ListItem>
              {item?.subMenu?.length && open && (
                <Collapse in={menuOpen[item.name]} timeout='auto' key={index} unmountOnExit>
                  <List component='div' disablePadding className='sub-menu'>
                    {item.subMenu.map((subMenu: TSubMenu, subMenuIndex: number) => (
                      <ListItem
                        key={subMenuIndex}
                        className={cx('sub-menu-item', {
                          active: activeScreen === subMenu.name,
                        })}
                      >
                        <ListItemButton>
                          <ListItemText
                            primary={t(`sidebar.${subMenu.name}`)}
                            onClick={() => handleNavigateDrawer(subMenu.url)}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          ))}
        </List>
      </Drawer>
      <Box component='main' className={cx('main')}>
        {hasPermission ? <Outlet /> : t('haveNotPagePermission')}
      </Box>
    </Box>
  ) : (
    <Navigate to={adminRouteAbsolute.login} />
  );
}
